import React, { useState, useEffect, useContext } from 'react';
import { fetchAllDuties, fetchCities } from '../utils/api';
import { UserDataContext } from '../context/UserContext';
import './ListView.css';

function DutyListView({ onLogout, onNavigateActiveDuties, onNavigateToMissions }) {
  const { user } = useContext(UserDataContext);
  const username = user?.name || '';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // City filter state
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [loadingCities, setLoadingCities] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize] = useState(50);

  useEffect(() => {
    const loadCities = async () => {
      setLoadingCities(true);
      const data = await fetchCities();
      if (data?.data && Array.isArray(data.data)) {
        setCities(data.data);
      } else if (Array.isArray(data)) {
        setCities(data);
      } else {
        setCities([]);
      }
      setLoadingCities(false);
    };
    loadCities();
  }, []);

  useEffect(() => {
    setCurrentPage(0); // Reset to first page when filters change
  }, [startDate, endDate, selectedCity]);

  useEffect(() => {
    loadAllDuties();
  }, [startDate, endDate, currentPage, selectedCity]);

  const loadAllDuties = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: currentPage,
        limit: pageSize,
      };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (selectedCity) params.city = selectedCity;

      const response = await fetchAllDuties(params);

      // Handle different response structures
      const duties = response.data.data || response.duties.data || response || [];
      setItems(Array.isArray(duties) ? duties : []);

      // Set total count if available in response
      if (response.data.total !== undefined) {
        setTotalItems(response.data.total);
      } else if (response.data.count !== undefined) {
        setTotalItems(response.data.count);
      } else {
        // Estimate: if we got a full page, there might be more
        setTotalItems(Array.isArray(duties) && duties.length === pageSize
          ? (currentPage + 2) * pageSize
          : (currentPage * pageSize) + (Array.isArray(duties) ? duties.length : 0));
      }
    } catch (err) {
      setError(err.message || 'Failed to load active duties. Please try again.');
      console.error('Error loading active duties:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    const title = (item.title || item.name || item.dutyName || item.dutyId || '').toLowerCase();
    const description = (item.description || item.details || '').toLowerCase();
    const district = (item.district || item.mission_duty?.district || '').toLowerCase();
    const collectorName = (item.collector?.name || item.mission_duty?.collector?.name || '').toLowerCase();

    return title.includes(searchLower) ||
      description.includes(searchLower) ||
      district.includes(searchLower) ||
      collectorName.includes(searchLower);
  });

  return (
    <div className="list-view-container">
      <header className="list-header">
        <div className="header-content">
          <h1 className="list-title">All Duties View</h1>
          <div className="user-info">
            {onNavigateActiveDuties && (
              <button
                onClick={onNavigateActiveDuties}
                className="nav-button"
              >
                Active Duties
              </button>
            )}
            {onNavigateToMissions && (
              <button
                onClick={onNavigateToMissions}
                className="nav-button"
              >
                Missions
              </button>
            )}
            <span className="welcome-text">Welcome, {username}!</span>
            <button onClick={onLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="list-content">
        <div className="filters-container">
          {/*<div className="search-bar">
            <input
              type="text"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <span className="search-icon">🔍</span>
          </div> */}

          <div className="date-filters">
            {cities.length > 0 && (
              <div className="date-filter-group">
                <label htmlFor="city-select" className="date-label">City:</label>
                <select
                  id="city-select"
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  className="date-input"
                  disabled={loadingCities}
                >
                  <option value="">All Cities</option>
                  {cities.map((city, index) => (
                    <option key={index} value={city}>{city}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="date-filter-group">
              <label htmlFor="start-date" className="date-label">Start Date:</label>
              <input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="date-input"
              />
            </div>
            {endDate && (
              <div className="date-filter-group">
                <label htmlFor="end-date" className="date-label">End Date:</label>
                <input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="date-input"
                />
              </div>
            )}
            {(startDate || endDate || selectedCity) && (
              <button
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setSelectedCity('');
                }}
                className="clear-date-button"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        <div className="list-stats">
          <span className="stat-item">Total Active Duties: {items.length}</span>
          <span className="stat-item">Showing: {filteredItems.length}</span>
        </div>

        {error && (
          <div className="error-message">
            {error}
            <button onClick={loadAllDuties} className="retry-button">
              Retry
            </button>
          </div>
        )}

        <div className="table-container">
          {loading ? (
            <div className="loading-state">
              <p>Loading active duties...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="empty-state">
              <p>{searchTerm ? 'No active duties found matching your search.' : 'No active duties available.'}</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Duty ID</th>
                  <th>Collector Name</th>
                  <th>City</th>
                  <th>DsId</th>
                  <th>Start Time</th>
                  <th>Stop Time</th>
                  <th>Duty Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id || item._id || item.dutyId} className="table-row">
                    <td>{item.dutyId || item.id || '-'}</td>
                    <td>{item.collector?.name || item.mission_duty?.collector?.name || '-'}</td>
                    <td>{item.collector?.city || item.mission_duty?.collector?.city || '-'}</td>
                    <td>{item.collector?.dsid || item.mission_duty?.collector?.dsid || '-'}</td>
                    <td>{item.start_time || item.mission_duty?.start_time ? new Date(item.start_time || item.mission_duty.start_time).toLocaleString() : '-'}</td>
                    <td>{item.stop_time || item.mission_duty?.stop_time ? new Date(item.stop_time || item.mission_duty.stop_time).toLocaleString() : '-'}</td>
                    <td>
                      {item.stop_time ? <span className="status-badge status-completed">
                        Completed
                      </span> : <span className="status-badge status-active">
                        Active
                      </span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Controls */}
        {!loading && filteredItems.length > 0 && (
          <div className="pagination-container">
            <button
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
              className="pagination-button"
            >
              Previous
            </button>

            <span className="pagination-info">
              Page {currentPage + 1} of {Math.max(1, Math.ceil(totalItems / pageSize))}
            </span>

            <button
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={items.length < pageSize}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default DutyListView;